import { useTill } from "@/lib/till-context";
import { addMonths, date, dateTime, fmtSerial, money } from "@/lib/format";
import { METHOD_LABEL, type Sale, type ZReport } from "@/lib/types";
import { tierTag } from "@/lib/pricing";

/** 80 mm receipt, printed through the browser (works with any printer the device knows). */
export function ReceiptPrint({ sale, tillName }: { sale: Sale; tillName?: string }) {
  const { store, me } = useTill();
  const lineDisc = sale.lines.reduce((n, l) => n + l.discount, 0);
  return (
    <div className="print-only">
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{store?.storeName ?? "Store"}</div>
        {me?.settings.receiptHeader ? <div style={{ whiteSpace: "pre-line" }}>{me.settings.receiptHeader}</div> : null}
      </div>
      <Hr />
      <div>Sale {sale.number}</div>
      <div>{dateTime(sale.createdAt)}</div>
      <div>
        {tillName ?? "Till"} · {sale.staffName}
      </div>
      {sale.customerName || sale.customerPhone ? <div>Customer: {sale.customerName ?? sale.customerPhone}</div> : null}
      {sale.pricingTier && sale.pricingTier !== "retail" ? <div style={{ fontWeight: 700 }}>Prices: {sale.pricingTier === "walkin" ? "WHOLESALE (walk-in)" : sale.pricingTier.toUpperCase()}</div> : null}
      <Hr />
      {sale.lines.map((l, i) => (
        <div key={i} style={{ marginBottom: 4 }}>
          <div>{l.name}</div>
          <Row a={`  ${l.qty} x ${money(l.price)}`} b={money(l.price * l.qty - l.discount)} />
          {tierTag(l.tier) && l.listPrice != null ? <div>  {tierTag(l.tier)!.toLowerCase()} (retail {money(l.listPrice)})</div> : null}
          {l.discount ? <div>  discount -{money(l.discount)}</div> : null}
          {l.serials.map((sn) => (
            <div key={sn}>
              {"  "}IMEI/SN {fmtSerial(sn)}
              {l.warrantyMonths ? ` · warranty to ${date(addMonths(sale.createdAt, l.warrantyMonths))}` : ""}
            </div>
          ))}
        </div>
      ))}
      <Hr />
      <Row a="Subtotal" b={money(sale.subtotal - lineDisc)} />
      {sale.discount ? <Row a={`Discount${sale.discountNote ? ` (${sale.discountNote})` : ""}`} b={`-${money(sale.discount)}`} /> : null}
      {(() => {
        const saved = sale.lines.reduce((n, l) => n + (tierTag(l.tier) && l.listPrice != null ? (l.listPrice - l.price) * l.qty : 0), 0);
        return saved > 0 ? <Row a="You saved" b={money(saved)} /> : null;
      })()}
      <Row a={<b>TOTAL</b>} b={<b>{money(sale.total)}</b>} />
      {sale.payments.map((p, i) => (
        <div key={i}>
          <Row a={`${METHOD_LABEL[p.method]}${p.last4 ? ` ****${p.last4}` : ""}${p.ref ? ` ${p.ref}` : ""}`} b={money(p.tendered ?? p.amount)} />
          {p.change ? <Row a="Change" b={money(p.change)} /> : null}
        </div>
      ))}
      <Hr />
      {me?.settings.receiptFooter ? <div style={{ textAlign: "center", whiteSpace: "pre-line" }}>{me.settings.receiptFooter}</div> : null}
      <div style={{ textAlign: "center", marginTop: 6 }}>Thank you!</div>
    </div>
  );
}

export function ZPrint({ z }: { z: ZReport }) {
  const { store } = useTill();
  const s = z.summary;
  return (
    <div className="print-only">
      <div style={{ textAlign: "center", fontWeight: 700, fontSize: 15 }}>{store?.storeName ?? "Store"}</div>
      <div style={{ textAlign: "center" }}>Z REPORT {z.number}</div>
      <Hr />
      <div>
        {z.tillName ?? "Till"} · {z.staffName}
      </div>
      <div>Opened {dateTime(z.openedAt)}</div>
      {z.closedAt ? <div>Closed {dateTime(z.closedAt)}</div> : null}
      <Hr />
      {s ? (
        <>
          <Row a={`Sales (${s.sales})`} b={money(s.takings)} />
          <Row a="Discounts" b={money(s.discounts)} />
          <Row a={`Refunds (${s.refunds})`} b={money(s.refundTotal)} />
          {s.devices ? <Row a="Devices sold" b={String(s.devices)} /> : null}
          {s.deliveries && (s.deliveries.deliver || s.deliveries.collect) ? (
            <>
              <Row a={`Deliveries booked (${s.deliveries.deliver})`} b={money(s.deliveries.fees)} />
              <Row a="Yard collections" b={String(s.deliveries.collect)} />
            </>
          ) : null}
          {s.byTier && (s.byTier.wholesale.count || s.byTier.walkin.count || s.byTier.breaks.count) ? (
            <>
              <Hr />
              <Row a={`Retail (${s.byTier.retail.count})`} b={money(s.byTier.retail.amount)} />
              <Row a={`Quantity breaks (${s.byTier.breaks.count})`} b={money(s.byTier.breaks.amount)} />
              <Row a={`Wholesale customers (${s.byTier.wholesale.count})`} b={money(s.byTier.wholesale.amount)} />
              <Row a={`Walk-in wholesale (${s.byTier.walkin.count})`} b={money(s.byTier.walkin.amount)} />
              <Row a="Below retail" b={money(s.byTier.retail.belowRetail + s.byTier.breaks.belowRetail + s.byTier.wholesale.belowRetail + s.byTier.walkin.belowRetail)} />
              {(s.walkIns ?? []).map((w) => <div key={w.sale}>  {w.sale} {w.name ?? ""} · ok {w.approvedBy ?? "?"}</div>)}
            </>
          ) : null}
          <Hr />
          {Object.entries(s.byMethod).map(([m, v]) => (
            <Row key={m} a={`${METHOD_LABEL[m as keyof typeof METHOD_LABEL] ?? m} (${v.count})`} b={money(v.amount)} />
          ))}
          <Hr />
          <Row a="Opening float" b={money(s.openingFloat)} />
          <Row a="Cash sales" b={money(s.cashSales)} />
          <Row a="Cash refunds" b={`-${money(s.cashRefunds)}`} />
          <Row a="Cash drops" b={`-${money(s.drops)}`} />
          <Row a="Paid in / out" b={`${money(s.paidIn)} / -${money(s.paidOut)}`} />
        </>
      ) : null}
      <Row a={<b>Expected cash</b>} b={<b>{money(z.expected)}</b>} />
      <Row a="Counted" b={money(z.counted)} />
      <Row a={<b>Over / short</b>} b={<b>{money(z.difference)}</b>} />
      {z.note ? <div>Note: {z.note}</div> : null}
      {z.approvedBy ? <div>Signed off: {z.approvedBy}</div> : null}
    </div>
  );
}

const Hr = () => <div style={{ borderTop: "1px dashed #000", margin: "5px 0" }} />;
const Row = ({ a, b }: { a: React.ReactNode; b: React.ReactNode }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
    <span>{a}</span>
    <span>{b}</span>
  </div>
);
